import { useEffect, useState, useRef, useCallback } from "react";
import { useProjectLocationStore } from "../hooks/useProjectLocationStore";
import { useProjectLocationAction } from "../hooks/useProjectLocationAction";
import FormActions from '../../../components/FormActions';
import { formFieldClass, formShell } from "../../../styles/formShell";

const ProjectLocationForm = ({ projectId, onSuccess, onError }) => {
    const { location, updateLocation } = useProjectLocationStore();
    const { fetchProjectLocation, updateProjectLocation } = useProjectLocationAction(projectId);
    const [isLoading, setIsLoading] = useState(false);
    const [originalLocation, setOriginalLocation] = useState(null);
    const [errors, setErrors] = useState({});
    const initialized = useRef(false);
    const lastProjectIdRef = useRef(null);

    useEffect(() => {
        if (!projectId) return;

        const projectIdChanged = lastProjectIdRef.current !== projectId;
        if (projectIdChanged) {
            initialized.current = false;
            lastProjectIdRef.current = projectId;
            updateLocation({ latitude: '', longitude: '', zoom: 15 });
            setOriginalLocation(null);
        }

        if (initialized.current) return;
        initialized.current = true;

        const loadProjectLocation = async () => {
            try {
                setIsLoading(true);
                const loc = await fetchProjectLocation();
                const locationData = {
                    latitude: loc.latitude,
                    longitude: loc.longitude,
                    zoom: loc.zoom
                };
                updateLocation(locationData);
                setOriginalLocation(locationData);
            } catch (error) {
                console.error('Failed to load project location:', error);
            } finally {
                setIsLoading(false);
            }
        };

        loadProjectLocation();
    }, [projectId, fetchProjectLocation, updateLocation]);

    const validateField = useCallback((field, value) => {
        let error = '';

        if (value === null || value === undefined || value === '') {
            error = '必須項目です';
        } else {
            const numValue = parseFloat(value);
            if (isNaN(numValue)) {
                error = '数値を入力してください';
            } else {
                if (field === 'latitude') {
                    if (numValue < -90 || numValue > 90) {
                        error = '-90から90の範囲で入力してください';
                    }
                } else if (field === 'longitude') {
                    if (numValue < -180 || numValue > 180) {
                        error = '-180から180の範囲で入力してください';
                    }
                } else if (field === 'zoom') {
                    if (numValue < 5 || numValue > 22) {
                        error = '5から22の範囲で入力してください';
                    }
                }
            }
        }

        setErrors(prev => ({
            ...prev,
            [field]: error
        }));

        return error === '';
    }, []);

    const handleSubmit = useCallback(async (e) => {
        e.preventDefault();

        const latitudeValid = validateField('latitude', location.latitude);
        const longitudeValid = validateField('longitude', location.longitude);
        const zoomValid = validateField('zoom', location.zoom);

        if (!latitudeValid || !longitudeValid || !zoomValid) {
            return;
        }

        try {
            setIsLoading(true);
            await updateProjectLocation(location.latitude, location.longitude, location.zoom);
            setOriginalLocation({
                latitude: location.latitude,
                longitude: location.longitude,
                zoom: location.zoom
            });
            setErrors({});

            if (onSuccess) {
                onSuccess();
            }
        } catch (error) {
            console.error('Failed to update project location:', error);

            if (onError) {
                onError();
            }
        } finally {
            setIsLoading(false);
        }
    }, [location, updateProjectLocation, onSuccess, onError, validateField]);

    const handleInputChange = useCallback((field, value) => {
        const numValue = value === '' ? '' : (field === 'zoom' ? parseInt(value, 10) : parseFloat(value));
        updateLocation({ ...location, [field]: isNaN(numValue) ? value : numValue });
        validateField(field, value);
    }, [location, updateLocation, validateField]);

    const handleCancel = useCallback(() => {
        if (originalLocation) {
            const resetLocation = {
                latitude: originalLocation.latitude,
                longitude: originalLocation.longitude,
                zoom: originalLocation.zoom
            };
            updateLocation(resetLocation);
            setOriginalLocation(resetLocation);
            setErrors({});
        }
    }, [originalLocation, updateLocation]);

    const hasChanges = useCallback(() => {
        if (!originalLocation) return false;
        const locLat = location.latitude === '' || isNaN(parseFloat(location.latitude)) ? '' : parseFloat(location.latitude);
        const origLat = originalLocation.latitude === '' || isNaN(parseFloat(originalLocation.latitude)) ? '' : parseFloat(originalLocation.latitude);
        const locLng = location.longitude === '' || isNaN(parseFloat(location.longitude)) ? '' : parseFloat(location.longitude);
        const origLng = originalLocation.longitude === '' || isNaN(parseFloat(originalLocation.longitude)) ? '' : parseFloat(originalLocation.longitude);
        const locZoom = location.zoom === '' || isNaN(parseInt(location.zoom, 10)) ? '' : parseInt(location.zoom, 10);
        const origZoom = originalLocation.zoom === '' || isNaN(parseInt(originalLocation.zoom, 10)) ? '' : parseInt(originalLocation.zoom, 10);

        return locLat !== origLat || locLng !== origLng || locZoom !== origZoom;
    }, [location, originalLocation]);

    const isValidForm = location.latitude && location.longitude && location.zoom >= 5 && location.zoom <= 22;

    return (
        <form className={formShell.form} onSubmit={handleSubmit} noValidate>
            <div className={formShell.grid3}>
                <div className={formShell.group}>
                    <label htmlFor="latitude" className={formShell.label}>緯度</label>
                    <input
                        id="latitude"
                        type="number"
                        step="0.000001"
                        min="-90"
                        max="90"
                        value={location.latitude}
                        onChange={(e) => handleInputChange("latitude", e.target.value)}
                        onBlur={(e) => validateField("latitude", e.target.value)}
                        disabled={isLoading}
                        placeholder="例: 35.6762"
                        className={formFieldClass(Boolean(errors.latitude), "input", true)}
                    />
                    {errors.latitude ? (
                        <p className={formShell.error}>{errors.latitude}</p>
                    ) : null}
                </div>

                <div className={formShell.group}>
                    <label htmlFor="longitude" className={formShell.label}>経度</label>
                    <input
                        id="longitude"
                        type="number"
                        step="0.000001"
                        min="-180"
                        max="180"
                        value={location.longitude}
                        onChange={(e) => handleInputChange("longitude", e.target.value)}
                        onBlur={(e) => validateField("longitude", e.target.value)}
                        disabled={isLoading}
                        placeholder="例: 139.6503"
                        className={formFieldClass(Boolean(errors.longitude), "input", true)}
                    />
                    {errors.longitude ? (
                        <p className={formShell.error}>{errors.longitude}</p>
                    ) : null}
                </div>

                <div className={formShell.group}>
                    <label htmlFor="zoom" className={formShell.label}>ズーム</label>
                    <input
                        id="zoom"
                        type="number"
                        min="5"
                        max="22"
                        step="1"
                        value={location.zoom}
                        onChange={(e) => handleInputChange("zoom", e.target.value)}
                        onBlur={(e) => validateField("zoom", e.target.value)}
                        disabled={isLoading}
                        placeholder="5-22"
                        className={formFieldClass(Boolean(errors.zoom), "input", true)}
                    />
                    {errors.zoom ? (
                        <p className={formShell.error}>{errors.zoom}</p>
                    ) : null}
                </div>
            </div>

            <FormActions
                hasChanges={hasChanges()}
                loading={isLoading}
                onReset={handleCancel}
                onSave={handleSubmit}
                submitType="submit"
                disabled={!isValidForm}
            />
        </form>
    );
};

export default ProjectLocationForm;
